import type { ButtonHTMLAttributes } from 'react';

/**
 * The prototype's `.btn`: white with a line border by default (cyan on hover), `primary` in ink,
 * `cyan`, `ghost` without a border, `danger` text; `sm` for table and card headers.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'cyan' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  busy?: boolean;
}

const LOOKS: Record<ButtonVariant, string> = {
  primary: 'border-ink bg-ink text-white hover:bg-navy3 hover:border-navy3',
  secondary: 'border-line2 bg-panel text-ink hover:border-cyan',
  cyan: 'border-cyan bg-cyan text-white hover:bg-cyan/90',
  ghost: 'border-transparent bg-transparent text-ink2 hover:bg-hover',
  danger: 'border-line2 bg-panel text-red hover:border-red',
};

export function buttonClass(variant: ButtonVariant = 'primary', size: 'md' | 'sm' = 'md'): string {
  const sizing = size === 'sm' ? 'rounded-[7px] px-[9px] py-1 text-xs' : 'rounded-lg px-3 py-[7px] text-[12.5px]';
  return `inline-flex items-center justify-center gap-1.5 whitespace-nowrap border font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${sizing} ${LOOKS[variant]}`;
}

export function Button({ variant = 'primary', size = 'md', busy, className = '', children, ...rest }: ButtonProps) {
  return (
    <button {...rest} disabled={rest.disabled ?? busy} className={`${buttonClass(variant, size)} ${className}`}>
      {busy ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" /> : null}
      {children}
    </button>
  );
}
